import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { WarningIcon as Warning, ArrowRightIcon as ArrowRight, XIcon as X } from "@phosphor-icons/react";
import { Indicium, Icon } from "@/components/luxury";

/**
 * Slim alert ribbon shown at the top of /dashboard when the admin
 * has pending operational items. Polls /admin/overview on mount.
 *
 * Visibility rules:
 *   - Renders only for users with role=admin.
 *   - Renders only when pending_total > 0.
 *   - Once dismissed, hidden for the rest of the session (sessionStorage).
 *     The badge on the nav still updates so nothing slips through.
 */
const SESSION_KEY = "admin_alert_dismissed";

export default function AdminAlertStrip({ user }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    try { if (sessionStorage.getItem(SESSION_KEY) === "1") setDismissed(true); } catch {}
    let cancelled = false;
    api.get("/admin/overview")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent — dashboard shouldn't crash on this */ });
    return () => { cancelled = true; };
  }, [user?.role]);

  if (user?.role !== "admin") return null;
  if (dismissed) return null;
  if (!data) return null;
  if ((data.pending_total || 0) === 0) return null;

  const q = data.queues;
  const parts = [];
  if (q.pending_transfers) parts.push(`${q.pending_transfers} transfer${q.pending_transfers === 1 ? "" : "s"}`);
  if (q.pending_pedigrees) parts.push(`${q.pending_pedigrees} pedigree submission${q.pending_pedigrees === 1 ? "" : "s"}`);
  if (q.disputed_escrow) parts.push(`${q.disputed_escrow} disputed escrow${q.disputed_escrow === 1 ? "" : "s"}`);
  if (q.unread_contacts) parts.push(`${q.unread_contacts} unread message${q.unread_contacts === 1 ? "" : "s"}`);
  if (q.open_reports) parts.push(`${q.open_reports} open report${q.open_reports === 1 ? "" : "s"}`);

  return (
    <div
      className="relative mb-6 rounded-xl bg-ink text-white border border-white/10 shadow-luxury-dark px-5 py-4 flex items-start sm:items-center gap-4 flex-col sm:flex-row overflow-hidden"
      data-testid="admin-alert-strip"
    >
      {/* Slim crimson side accent — the one genuinely urgent thing on
          the page reads as urgent, not as a styled banner. */}
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-crimson" />
      <Indicium icon={Warning} tone="crimson" size="md" variant="dark" />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold leading-snug font-tabular">
          {data.pending_total} admin item{data.pending_total === 1 ? "" : "s"} waiting
        </p>
        <p className="text-[11px] text-white/60 mt-0.5 truncate">
          {parts.join(" · ")}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/admin"
          data-testid="admin-alert-open-console"
          className="inline-flex items-center gap-1.5 bg-gold-gradient hover:opacity-95 text-ink rounded-full h-8 px-4 text-[12px] font-semibold transition-opacity shadow-[0_4px_14px_rgba(201,162,39,0.3)]"
        >
          Open admin console
          <Icon as={ArrowRight} tone="current" size="xs" weight="bold" />
        </Link>
        <button
          type="button"
          onClick={() => {
            try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_) {}
            setDismissed(true);
          }}
          data-testid="admin-alert-dismiss"
          className="text-white/55 hover:text-white p-1.5 transition-colors"
          aria-label="Dismiss alert (will reappear next session)"
        >
          <Icon as={X} tone="current" size="xs" weight="bold" />
        </button>
      </div>
    </div>
  );
}
